// usage {{json-pretty-print someJson}}
// see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#The_space_argument

type JSONValue =
  string | number | boolean | null | { [key: string]: JSONValue } | JSONValue[] | unknown;
type Options = {
  level?: number;
  isOpen?: boolean;
};

/**
 * HTML-escape a string to prevent XSS attacks when rendering JSON values.
 * Escapes: & < > " ' /
 */
function escapeHtml(str: string): string {
  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  return str.replace(/[&<>"'/]/g, (char) => htmlEscapes[char]!);
}

function renderJson(value: JSONValue, options?: Options): string {
  const level = options?.level || 0;
  const isOpen = options?.isOpen ?? true;

  if (typeof value === 'string') {
    return `<span class="text-green-600 dark:text-green-400">"${escapeHtml(value)}"</span>`;
  }
  if (typeof value === 'number') {
    return `<span class="text-orange-600 dark:text-orange-400">${value}</span>`;
  }
  if (typeof value === 'boolean') {
    return `<span class="text-blue-600 dark:text-blue-400">${value}</span>`;
  }
  if (value === null) {
    return `<span class="text-purple-600 dark:text-purple-400">null</span>`;
  }
  if (Array.isArray(value)) {
    return `<details class="ml-${level * 4}" ${isOpen ? 'open' : ''}><summary class="cursor-pointer text-gray-800 dark:text-gray-200 hover:text-gray-600 dark:hover:text-gray-400">[Array(${value.length})]</summary><div class="ml-4 border-l border-gray-300 dark:border-gray-600 pl-4">${value.map((item) => renderJson(item, { level: level + 1, isOpen })).join('')}</div></details>`;
  }
  if (typeof value === 'object') {
    return `<details class="ml-${level * 4}" ${isOpen ? 'open' : ''}><summary class="cursor-pointer text-gray-800 dark:text-gray-200 hover:text-gray-600 dark:hover:text-gray-400">{Object}</summary><div class="ml-4 border-l border-gray-300 dark:border-gray-600 pl-4">${Object.entries(
      value
    )
      .map(
        ([key, val]) =>
          `<div><span class="text-red-600 dark:text-red-400">"${escapeHtml(key)}":</span> ${renderJson(
            val,
            {
              level: level + 1,
              isOpen,
            }
          )}</div>`
      )
      .join('')}</div></details>`;
  }
  return '';
}

export default function jsonPrettyPrint(json: JSONValue, options?: Options): string {
  return `<div class="font-mono text-sm bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600 p-3 rounded leading-tight">${renderJson(
    json,
    options
  )}</div>`;
}

interface JsonObject {
  [key: string]: JsonValue;
}

type JsonValue = string | number | boolean | null | JsonObject | JsonValue[];

interface Changes {
  [key: string]: [JsonValue, JsonValue];
}

export function compareJsonObjects<T extends JsonObject>(
  obj1: T,
  obj2: T,
  prefix: string = ''
): Changes {
  const changes: Changes = {};

  function addChange(key: string, value1: JsonValue, value2: JsonValue) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    changes[fullKey] = [value1, value2];
  }

  // `key` is the raw property key; `addChange` prepends `prefix` itself, so
  // callers must NOT pre-prefix it (doing so double-prefixed nested-array
  // paths, e.g. `a.a.arr`). The full path is only needed for the recursive
  // `compareJsonObjects` prefix below.
  function compareArrays(arr1: JsonValue[], arr2: JsonValue[], key: string) {
    if (arr1.length !== arr2.length) {
      addChange(key, arr1, arr2);
      return;
    }
    const fullKey = prefix ? `${prefix}.${key}` : key;
    for (let i = 0; i < arr1.length; i++) {
      // noUncheckedIndexedAccess types these as `JsonValue | undefined`;
      // coalesce once so comparison and storage use the same value.
      const item1 = arr1[i] ?? null;
      const item2 = arr2[i] ?? null;
      if (isObject(item1) && isObject(item2)) {
        const nestedChanges = compareJsonObjects(item1, item2, `${fullKey}[${i}]`);
        Object.assign(changes, nestedChanges);
      } else if (item1 !== item2) {
        addChange(`${key}[${i}]`, item1, item2);
      }
    }
  }

  for (const key in obj1) {
    if (Object.prototype.hasOwnProperty.call(obj1, key)) {
      const value1 = obj1[key] ?? null;
      if (Object.prototype.hasOwnProperty.call(obj2, key)) {
        const value2 = obj2[key] ?? null;
        if (isObject(value1) && isObject(value2)) {
          const nestedChanges = compareJsonObjects(
            value1,
            value2,
            prefix ? `${prefix}.${key}` : key
          );
          Object.assign(changes, nestedChanges);
        } else if (Array.isArray(value1) && Array.isArray(value2)) {
          compareArrays(value1, value2, key);
        } else if (value1 !== value2) {
          addChange(key, value1, value2);
        }
      } else {
        addChange(key, value1, null);
      }
    }
  }

  for (const key in obj2) {
    if (
      Object.prototype.hasOwnProperty.call(obj2, key) &&
      !Object.prototype.hasOwnProperty.call(obj1, key)
    ) {
      addChange(key, null, obj2[key] ?? null);
    }
  }

  return changes;
}

export function isObject(value: JsonValue): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
